import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { money, shortDate, balanceLabel } from "@/lib/format";
import { cn } from "@/lib/cn";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui-kit/Table";
import { useFunder } from "@/funder/FunderContext";

function Summary({ label, value, tone }) {
  return (
    <div className="px-4 py-2">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={cn("nums text-sm font-semibold", tone === "due" ? "text-foreground" : tone === "credit" ? "text-status-paid" : "text-foreground")}>
        {value}
      </p>
    </div>
  );
}

function PayerLedger({ title, data }) {
  if (!data) return null;
  const outstanding = data.running_balance || 0;
  return (
    <div className="rounded-lg border border-border bg-surface" data-testid={`statement-${data.payer.toLowerCase()}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-5 py-3">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <div className="flex flex-wrap divide-x divide-border">
          <Summary label="Billed" value={money(data.billed_to_date)} />
          <Summary label="Paid" value={money(data.payments_received)} />
          <Summary label={outstanding < 0 ? "Carried credit" : "Outstanding"} value={money(Math.abs(outstanding))} tone={outstanding < 0 ? "credit" : "due"} />
          <Summary label="Unbilled work" value={money(data.unbilled_work)} />
        </div>
      </div>
      <div className="p-2">
        {data.entries.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No charges or payments yet.</p>
        ) : (
          <Table>
            <THead><TR><TH>Date</TH><TH>Activity</TH><TH className="text-right">Charge</TH><TH className="text-right">Payment</TH><TH className="text-right">Balance</TH></TR></THead>
            <TBody>
              {data.entries.map((e, idx) => (
                <TR key={idx} data-testid={`statement-row-${data.payer.toLowerCase()}-${idx}`} className={e.void ? "opacity-60" : ""}>
                  <TD className="whitespace-nowrap text-muted-foreground">{shortDate(e.date)}</TD>
                  <TD className={e.void ? "text-muted-foreground line-through decoration-1" : "text-foreground"}>{e.description}</TD>
                  <TD className="nums text-right text-muted-foreground">{e.charge ? money(e.charge) : "—"}</TD>
                  <TD className="nums text-right text-status-paid">{e.payment ? money(e.payment) : "—"}</TD>
                  <TD className="nums text-right font-medium text-foreground">{balanceLabel(e.running_balance)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>
    </div>
  );
}

/**
 * The full per-matter reconciliation statement — both payer ledgers with running
 * balances, plus the client retainer balance. Read-only projection from the ledger
 * endpoint; same component renders embedded, standalone, and (Stage 6) to PDF.
 */
export function LedgerStatement({ matterId }) {
  const { abbr } = useFunder();
  const [data, setData] = useState(null);
  const [focus, setFocus] = useState("All");

  useEffect(() => {
    api.get(`/matters/${matterId}/statement`).then((r) => setData(r.data)).catch(() => {});
  }, [matterId]);

  if (!data) return <div className="grid place-items-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const tabs = data.is_acsa ? ["All", "Client", "ACSA"] : ["Client"];
  const label = (t) => (t === "ACSA" ? abbr : t);
  const showClient = focus === "All" || focus === "Client";
  const showAcsa = data.is_acsa && (focus === "All" || focus === "ACSA");

  return (
    <div className="space-y-4" data-testid="ledger-statement">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Client retainer balance: <span className="font-medium text-foreground" data-testid="statement-retainer">{money(data.retainer_balance)}</span>
        </p>
        {tabs.length > 1 && (
          <div className="inline-flex rounded-md border border-border p-0.5">
            {tabs.map((t) => (
              <button
                key={t}
                onClick={() => setFocus(t)}
                data-testid={`statement-focus-${t.toLowerCase()}`}
                className={cn("rounded px-3 py-1 text-sm transition-colors", focus === t ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}
              >
                {label(t)}
              </button>
            ))}
          </div>
        )}
      </div>

      {showClient && <PayerLedger title="Client" data={data.client} />}
      {showAcsa && <PayerLedger title={`${abbr} (funder)`} data={data.acsa} />}
    </div>
  );
}
